import {
  ApiResponse,
  ContactsResponse,
  PropertiesResponse,
  DocumentsData,
  ReportsResponse,
  InvoicesResponse,
  ThreadResponse,
  MessagesResponse,
  FacadeImageResponse,
  MessageSendData,
  ChatMessage
} from '../types/expertise-detail.types';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';

// Helper function to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// Helper function to make authenticated requests
const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token available');
  }

  return fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

// Cache implementation
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCachedData = <T>(key: string): T | null => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data as T;
  }
  cache.delete(key);
  return null;
};

const setCachedData = <T>(key: string, data: T): void => {
  cache.set(key, { data, timestamp: Date.now() });
};

// FileMaker API Endpoints

export const getAffaireContacts = async (
  affaireId: string
): Promise<ApiResponse<ContactsResponse>> => {
  const cacheKey = `contacts-${affaireId}`;
  const cached = getCachedData<ContactsResponse>(cacheKey);
  if (cached) {
    return { success: true, data: cached };
  }

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/fm/affaire/${affaireId}/contacts`
    );
    
    if (!response.ok) {
      // Return empty data for 404 or 500 errors
      if (response.status === 404 || response.status === 500) {
        console.warn(`Contacts not available for affaire ${affaireId} (${response.status})`);
        return { success: true, data: { contacts: [] } };
      }
      throw new Error(`Failed to fetch contacts: ${response.statusText}`);
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      setCachedData(cacheKey, result.data);
    }
    return result;
  } catch (error) {
    console.error('Error fetching contacts:', error);
    // Return empty data to prevent white screen
    return { success: true, data: { contacts: [] } };
  }
};

export const getAffaireBiens = async (
  affaireId: string
): Promise<ApiResponse<PropertiesResponse>> => {
  const cacheKey = `biens-${affaireId}`;
  const cached = getCachedData<PropertiesResponse>(cacheKey);
  if (cached) {
    return { success: true, data: cached };
  }

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/fm/affaire/${affaireId}/biens`
    );
    
    if (!response.ok) {
      // Return empty data for 404 or 500 errors
      if (response.status === 404 || response.status === 500) {
        console.warn(`Properties not available for affaire ${affaireId} (${response.status})`);
        return { success: true, data: { biens: [] } };
      }
      throw new Error(`Failed to fetch properties: ${response.statusText}`);
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      setCachedData(cacheKey, result.data);
    }
    return result;
  } catch (error) {
    console.error('Error fetching properties:', error);
    // Return empty data to prevent white screen
    return { success: true, data: { biens: [] } };
  }
};

export const getAffaireDocuments = async (
  affaireId: string
): Promise<ApiResponse<DocumentsData>> => {
  const cacheKey = `documents-${affaireId}`;
  const cached = getCachedData<DocumentsData>(cacheKey);
  if (cached) {
    return { success: true, data: cached };
  }

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/fm/affaire/${affaireId}/documents`
    );
    
    if (!response.ok) {
      // Return empty data for 404 or 500 errors instead of throwing
      if (response.status === 404 || response.status === 500) {
        console.warn(`Documents not available for affaire ${affaireId} (${response.status})`);
        return {
          success: true,
          data: {
            requested_documents: { documents: [] },
            checked_documents: { documents: [] },
          }
        };
      }
      throw new Error(`Failed to fetch documents: ${response.statusText}`);
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      setCachedData(cacheKey, result.data);
    }
    return result;
  } catch (error) {
    console.error('Error fetching documents:', error);
    // Return empty data structure instead of error to prevent white screen
    return {
      success: true,
      data: {
        requested_documents: { documents: [] },
        checked_documents: { documents: [] },
      }
    };
  }
};

export const getAffaireReports = async (
  affaireId: string
): Promise<ApiResponse<ReportsResponse>> => {
  const cacheKey = `reports-${affaireId}`;
  const cached = getCachedData<ReportsResponse>(cacheKey);
  if (cached) {
    return { success: true, data: cached };
  }

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/fm/affaire/${affaireId}/reports`
    );
    
    if (!response.ok) {
      // Handle 500 errors as empty data (as per documentation)
      if (response.status === 500) {
        const emptyData: ReportsResponse = {
          reports: {
            expertise: [],
            prerapport: [],
          },
        };
        return { success: true, data: emptyData };
      }
      throw new Error(`Failed to fetch reports: ${response.statusText}`);
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      setCachedData(cacheKey, result.data);
    }
    return result;
  } catch (error) {
    console.error('Error fetching reports:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to fetch reports',
    };
  }
};

export const getAffaireFactures = async (
  affaireId: string
): Promise<ApiResponse<InvoicesResponse>> => {
  const cacheKey = `factures-${affaireId}`;
  const cached = getCachedData<InvoicesResponse>(cacheKey);
  if (cached) {
    return { success: true, data: cached };
  }

  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/fm/affaire/${affaireId}/factures`
    );
    
    if (!response.ok) {
      // Handle 500 errors as empty data (as per documentation)
      if (response.status === 500) {
        const emptyData: InvoicesResponse = {
          factures: [],
        };
        return { success: true, data: emptyData };
      }
      throw new Error(`Failed to fetch invoices: ${response.statusText}`);
    }
    
    const result = await response.json();
    if (result.success && result.data) {
      setCachedData(cacheKey, result.data);
    }
    return result;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to fetch invoices',
    };
  }
};

export const getAffaireFacadeImage = async (
  affaireId: string
): Promise<ApiResponse<FacadeImageResponse>> => {
  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/fm/affaire/${affaireId}/facade`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch facade image: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching facade image:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to fetch facade image',
    };
  }
};

// Chat API Endpoints

export const getOrCreateThread = async (
  affaireId: string
): Promise<ApiResponse<ThreadResponse>> => {
  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/chat/threads/affaire/${affaireId}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get/create thread: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting/creating thread:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get/create thread',
    };
  }
};

export const getThreadMessages = async (
  threadId: string,
  page: number = 1,
  limit: number = 50
): Promise<ApiResponse<MessagesResponse>> => {
  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/chat/threads/${threadId}/messages?page=${page}&limit=${limit}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch messages: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching messages:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to fetch messages',
    };
  }
};

export const sendMessage = async (
  threadId: string,
  data: MessageSendData
): Promise<ApiResponse<{ message: ChatMessage }>> => {
  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/api/chat/threads/${threadId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to send message: ${response.statusText}`);
    }
    
    const result = await response.json();
    
    // Invalidate messages cache after sending
    cache.delete(`messages-${threadId}`);
    
    return result;
  } catch (error) {
    console.error('Error sending message:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to send message',
    };
  }
};

// Document upload
export const uploadDocument = async (
  affaireId: string,
  documentId: string,
  file: File
): Promise<ApiResponse<any>> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentId', documentId);
    
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    const response = await fetch(
      `${API_BASE_URL}/api/fm/affaire/${affaireId}/documents/upload`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to upload document: ${response.statusText}`);
    }
    
    const result = await response.json();
    
    // Invalidate documents cache after upload
    cache.delete(`documents-${affaireId}`);
    
    return result;
  } catch (error) {
    console.error('Error uploading document:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to upload document',
    };
  }
};

// Download helper with authentication
export const downloadFile = async (
  downloadUrl: string,
  fileName: string
): Promise<void> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    const response = await fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

// Cache management utilities
export const clearCache = (): void => {
  cache.clear();
};

export const invalidateCacheForAffaire = (affaireId: string): void => {
  const keysToDelete: string[] = [];
  cache.forEach((_, key) => {
    if (key.includes(affaireId)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => cache.delete(key));
};